import { CommonModule } from '@angular/common';
import { AfterViewInit, ApplicationRef, Component, ComponentRef, createComponent, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { ThemeService } from '../../services/theme.service';
import { FooterComponent } from '../footer/footer.component';
import { TranslationSyncService } from '../../services/translation-sync.service';
import { CubePortfolioComponent } from "./entries/cube-portfolio/cube-portfolio.component";
import { DarkLightModeComponent } from './entries/dark-light-mode/dark-light-mode.component';
import { DesignDecisionComponent } from './entries/design-decision/design-decision.component';
import { DesignIntentionComponent } from './entries/design-intention/design-intention.component';
import { CoolifyComponent } from './entries/coolify/coolify.component';
import { EtlInFinanceComponent } from './entries/etl-in-finance/etl-in-finance.component';
import { ScrollStateService } from '../../services/scroll-state.service';
import { BLOG_CONTENT } from './content/blog-content';
import { BlogEntry } from './content/content.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HideShellOnScrollDirective } from '../../directives/hide-shell-on-scroll.directive';
import { Subject, takeUntil } from 'rxjs';
import { layoutMasonryGrid, MasonryOptions } from './shared/blog-masonry.util';


@Component({
  selector: 'app-blog',
  imports: [
    CommonModule,
    FooterComponent,
    CubePortfolioComponent,
    DarkLightModeComponent,
    DesignDecisionComponent,
    DesignIntentionComponent,
    CoolifyComponent,
    EtlInFinanceComponent,
    TranslateModule,
    HideShellOnScrollDirective],
  templateUrl: './blog.component.html',
  styleUrls: ['./blog.component.css'],
  encapsulation: ViewEncapsulation.None // wird benoetigt fuer dark container
})
export class BlogComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() activeFace: number = 0;
  @Output() toggleImprint = new EventEmitter<void>();
  @Output() togglePrivacy = new EventEmitter<void>();

  @ViewChild('containerBlog', { static: false }) containerRef!: ElementRef<HTMLElement>;
  // @ViewChild('blogPanel', { static: true }) blogPanelRef!: ElementRef<HTMLElement>;
  @ViewChild('blogGrid', { static: true }) blogGridRef!: ElementRef<HTMLElement>;
  @ViewChild('blogDetailsPanel', { static: true }) blogDetailsRef!: ElementRef<HTMLElement>;

  content: { blog: BlogEntry[] } = { blog: BLOG_CONTENT }; // eine Quelle für Light & Dark
  posts: Array<{ TITLE: string; DESCRIPTION: string }> = [];

  isDarkMode: boolean = false;
  darkMode: boolean;

  private darkContainer!: HTMLElement;
  activeBlogIndex: number | null = null;

  private currentViewState: 'list' | 'details' = 'list';

  private entryRefs: ComponentRef<any>[] = [];

  private entryComponents = [
    CubePortfolioComponent,
    DarkLightModeComponent,
    DesignDecisionComponent,
    DesignIntentionComponent,
    CoolifyComponent,
    EtlInFinanceComponent
  ];

  private destroy$ = new Subject<void>();

  private readonly masonryOptions: MasonryOptions = {
    itemSelector: '.blog-item',
    minColumnWidth: 320,
    gap: 24,
    maxColumns: 3
  };

  private masonryRaf: number | null = null;
  private resizeObserver?: ResizeObserver;

  constructor(
    private renderer: Renderer2,
    public themeService: ThemeService,
    private sync: TranslationSyncService,
    private translate: TranslateService,
    private appRef: ApplicationRef,
    public scroll: ScrollStateService
  ) { }

  ngOnInit(): void {
    this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.isDarkMode = mode;
        this.updateTheme();
      });

    this.translate.get('BLOG.POSTS')
      .pipe(takeUntil(this.destroy$))
      .subscribe((content: any) => {
        this.posts = content;

        setTimeout(() => {

          if (this.blogGridRef?.nativeElement) {
            this.registerAll(this.blogGridRef.nativeElement);
          }

          this.scheduleMasonryLayout();
        }, 0);
      });
  }

  ngAfterViewInit(): void {
    const container = this.containerRef.nativeElement;
    this.darkContainer = document.getElementById('dark-container-blog') as HTMLElement;

    if (!container || !this.darkContainer) {
      return;
    }

    Promise.resolve().then(() => {

      if (this.blogGridRef?.nativeElement) {
        this.registerAll(this.blogGridRef.nativeElement);
      }

      this.registerAll(this.darkContainer);
    });

    // Dark-Container initial ausblenden
    this.renderer.removeClass(this.darkContainer, 'active');

    // Dark-Layer initial befüllen
    this.createDarkContainerBlog();
    this.registerAll(this.darkContainer);

    this.setupMasonryObservers();

    setTimeout(() => {
      this.updateTheme();
      this.scheduleMasonryLayout();

      this.scroll.bindShellHideOnScroll(
        '#dark-container-blog .blog-grid',
        'blog-list',
        { dock: true, nav: true, footer: true },
        true
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyEntryRefs();
    this.scroll.resetVisibility();
    this.destroy$.next();
    this.destroy$.complete();

    this.resizeObserver?.disconnect();

    if (this.masonryRaf !== null) {
      cancelAnimationFrame(this.masonryRaf);
      this.masonryRaf = null;
    }

    window.removeEventListener('resize', this.scheduleMasonryLayout);

    this.scroll.unbindShellHideOnScroll('#dark-container-blog .blog-grid');
    this.scroll.unbindShellHideOnScroll('#dark-container-blog .blog-details-panel .scroll-bleed');
  }


  private relayoutBlogGrids(): void {
    if (this.blogGridRef?.nativeElement) {
      layoutMasonryGrid(this.blogGridRef.nativeElement, this.masonryOptions);
    }

    const darkGrid = this.darkContainer?.querySelector('.blog-grid') as HTMLElement | null;
    if (darkGrid) {
      layoutMasonryGrid(darkGrid, this.masonryOptions);
    }
  }

  private scheduleMasonryLayout = (): void => {
    if (this.masonryRaf !== null) {
      cancelAnimationFrame(this.masonryRaf);
    }

    this.masonryRaf = requestAnimationFrame(() => {
      this.masonryRaf = null;
      this.relayoutBlogGrids();
    });
  };

  private setupMasonryObservers(): void {
    this.resizeObserver?.disconnect();

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleMasonryLayout();
    });

    if (this.blogGridRef?.nativeElement) {
      this.resizeObserver.observe(this.blogGridRef.nativeElement);
    }

    const darkGrid = this.darkContainer?.querySelector('.blog-grid') as HTMLElement | null;
    if (darkGrid) {
      this.resizeObserver.observe(darkGrid);
    }

    window.addEventListener('resize', this.scheduleMasonryLayout, { passive: true });

    if ((document as any).fonts?.ready) {
      (document as any).fonts.ready.then(() => {
        this.scheduleMasonryLayout();
      });
    }
  }

  openBlog(i: number): void {
    this.scroll.saveScrollPosition('blog-list', this.blogGridRef?.nativeElement ?? null);

    const darkGridBeforeOpen = this.darkContainer?.querySelector('.blog-grid') as HTMLElement | null;
    this.scroll.saveScrollPosition('blog-list', darkGridBeforeOpen);

    this.activeBlogIndex = i;
    this.currentViewState = 'details';

    this.blogGridRef.nativeElement.classList.add('hide');
    this.blogDetailsRef.nativeElement.classList.add('show');

    if (this.isDarkMode) {
      this.updateDarkDetailsPanel();

      const darkGrid = this.darkContainer.querySelector('.blog-grid');
      if (darkGrid) darkGrid.classList.add('hide');

      const darkDetails = this.darkContainer.querySelector('.blog-details-panel');
      if (darkDetails) darkDetails.classList.add('show');
    }

    requestAnimationFrame(() => {
      const lightDetails = this.getLightDetailsScrollEl();
      const darkDetails = this.getDarkDetailsScrollEl();

      this.scroll.restoreScrollPositionAndResetKey('blog-details', lightDetails);
      this.scroll.restoreScrollPositionAndResetKey('blog-details', darkDetails);
    });
  }

  closeBlog(): void {
    const lightDetails = this.getLightDetailsScrollEl();
    const darkDetails = this.getDarkDetailsScrollEl();

    this.scroll.saveScrollPosition('blog-details', lightDetails);
    this.scroll.saveScrollPosition('blog-details', darkDetails);

    this.activeBlogIndex = null;
    this.currentViewState = 'list';

    this.getAllPanels('.blog-grid').forEach(el => el.classList.remove('hide'));
    this.getAllPanels('.blog-details-panel').forEach(el => el.classList.remove('show'));

    this.destroyEntryRefs();
    this.scroll.resetVisibility();

    this.scheduleMasonryLayout();

    requestAnimationFrame(() => {
      const lightGrid = this.blogGridRef?.nativeElement ?? null;
      const darkGrid = this.getDarkGridEl();

      this.scroll.restoreScrollPositionAndResetKey('blog-list', lightGrid);
      this.scroll.restoreScrollPositionAndResetKey('blog-list', darkGrid);
    });
  }

private updateTheme(): void {
  if (!this.darkContainer || !this.containerRef?.nativeElement) {
    return;
  }

  const nextIsDark = this.isDarkMode;
  const prevIsDark = !nextIsDark;

  this.captureCurrentScrollStateForTheme(prevIsDark);

  if (this.isDarkMode && this.currentViewState === 'details') {
    this.updateDarkDetailsPanel();
  }

  this.themeService.toggleThemeMode(
    this.isDarkMode,
    this.renderer,
    this.containerRef.nativeElement,
    this.darkContainer
  );

  this.restoreViewState();
  this.registerAll(this.darkContainer);

  setTimeout(() => {
    this.scheduleMasonryLayout();

    requestAnimationFrame(() => {
      this.restoreSharedScrollStateForTheme(nextIsDark);
    });
  }, 0);
}

private captureCurrentScrollStateForTheme(isDarkThemeSource: boolean): void {
  let el: HTMLElement | null = null;

  if (this.currentViewState === 'list') {
    el = isDarkThemeSource ? this.getDarkGridEl() : this.getLightGridEl();
    this.scroll.saveScrollPosition('blog-list', el);
  } else {
    el = isDarkThemeSource ? this.getDarkDetailsScrollEl() : this.getLightDetailsScrollEl();
    this.scroll.saveScrollPosition('blog-details', el);
  }
}

private restoreSharedScrollStateForTheme(isDarkThemeTarget: boolean): void {
  let el: HTMLElement | null = null;

  if (this.currentViewState === 'list') {
    el = isDarkThemeTarget ? this.getDarkGridEl() : this.getLightGridEl();
    this.scroll.restoreScrollPositionAndResetKey('blog-list', el);
  } else {
    el = isDarkThemeTarget ? this.getDarkDetailsScrollEl() : this.getLightDetailsScrollEl();
    this.scroll.restoreScrollPositionAndResetKey('blog-details', el);
  }
}

  private restoreViewState(): void {
    if (this.currentViewState === 'details' && this.activeBlogIndex !== null) {
      if (this.isDarkMode) {
        const darkGrid = this.darkContainer.querySelector('.blog-grid');
        if (darkGrid) darkGrid.classList.add('hide');

        const darkDetails = this.darkContainer.querySelector('.blog-details-panel');
        if (darkDetails) darkDetails.classList.add('show');
      } else {
        this.blogGridRef.nativeElement.classList.add('hide');
        this.blogDetailsRef.nativeElement.classList.add('show');
      }
    } else {
      this.getAllPanels('.blog-grid').forEach(el => el.classList.remove('hide'));
      this.getAllPanels('.blog-details-panel').forEach(el => el.classList.remove('show'));
    }
  }

  private createDarkContainerBlog(): void {
    const container = document.getElementById('dark-container-blog');
    if (!container) return;

    const blogGrid = container.querySelector('.blog-grid');
    if (!blogGrid) return;

    // Dark-Grid leeren
    blogGrid.innerHTML = '';

    // Falls du lieber aus BLOG_CONTENT die Reihenfolge nimmst, passt das.
    // Wichtig ist nur, dass wir die i18n-Keys indexbasiert auf BLOG.POSTS bauen:
    this.content.blog.forEach((entry, i) => {
      const blogItem = document.createElement('div');
      blogItem.className = 'blog-item';

      const titleKey = `BLOG.POSTS.${i}.TITLE`;
      const descKey = `BLOG.POSTS.${i}.DESCRIPTION`;

      const title = document.createElement('h4');
      title.setAttribute('data-translate', titleKey);
      // Anti-Flicker: initiale Füllung (optional)
      title.textContent = this.translate.instant(titleKey);
      blogItem.appendChild(title);

      const desc = document.createElement('p');
      desc.setAttribute('data-translate', descKey);
      desc.textContent = this.translate.instant(descKey);
      blogItem.appendChild(desc);

      const iconDiv = document.createElement('div');
      iconDiv.className = 'blog-icon';

      const iconLink = document.createElement('a');
      iconLink.href = '#';
      iconLink.innerHTML = `<i class="bx bx-arrow-back"></i>`;
      iconLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.openBlog(i);
      });

      iconDiv.appendChild(iconLink);
      blogItem.appendChild(iconDiv);

      blogGrid.appendChild(blogItem);
    });

    // Jetzt, wo die data-translate-Attribute existieren, beim Sync registrieren:
    setTimeout(() => {
      this.registerAll(this.darkContainer);
      this.scheduleMasonryLayout();
    }, 0);

    // Details-Panel ggf. aktualisieren, wenn du bereits in einer Detailansicht bist
    if (this.currentViewState === 'details' && this.activeBlogIndex !== null) {
      this.updateDarkDetailsPanel();
    }
  }

  private updateDarkDetailsPanel(): void {
    if (!this.darkContainer) return;
    if (this.activeBlogIndex === null) return;

    const ComponentType = this.entryComponents[this.activeBlogIndex];
    if (!ComponentType) return;

    this.destroyEntryRefs();

    const darkDetailsPanel = this.darkContainer.querySelector('.blog-details-panel') as HTMLElement | null;
    if (!darkDetailsPanel) return;

    darkDetailsPanel.innerHTML = '';

    const scrollBleed = document.createElement('div');
    scrollBleed.className = 'scroll-bleed';

    const btnWrap = document.createElement('div');
    btnWrap.className = 'close-btn-wrapper';

    const btn = document.createElement('button');
    btn.className = 'close-btn';
    btn.textContent = '✕';
    btn.addEventListener('click', () => this.closeBlog());
    btnWrap.appendChild(btn);

    const detailsContent = document.createElement('div');
    detailsContent.className = 'details-content';

    scrollBleed.appendChild(btnWrap);
    scrollBleed.appendChild(detailsContent);
    darkDetailsPanel.appendChild(scrollBleed);

    const cmpRef = createComponent(ComponentType, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.appRef.injector
    });

    cmpRef.instance.darkMode = true;
    this.appRef.attachView(cmpRef.hostView);

    const domElem = (cmpRef.hostView as any).rootNodes[0] as HTMLElement;
    detailsContent.appendChild(domElem);

    this.entryRefs.push(cmpRef);

    setTimeout(() => {
      this.scroll.bindShellHideOnScroll(
        '#dark-container-blog .blog-details-panel .scroll-bleed',
        'blog-details',
        { dock: true, nav: true, footer: true },
        true
      );

      requestAnimationFrame(() => {
        this.restoreSharedScrollState();
      });
    }, 0);
  }

  private getAllPanels(selector: string): HTMLElement[] {
    const lightElem = this.containerRef?.nativeElement.querySelector(selector);
    const darkElem = this.darkContainer?.querySelector(selector);
    return [lightElem, darkElem].filter((el): el is HTMLElement => !!el);
  }

  private registerAll(root: HTMLElement) {
    const elems = Array.from(root.querySelectorAll<HTMLElement>('[data-translate]'));
    elems.forEach(el => {
      const key = el.getAttribute('data-translate')!;
      this.sync.register(el, key);
    });
  }
  private destroyEntryRefs() {
    for (const ref of this.entryRefs) {
      this.appRef.detachView(ref.hostView);
      ref.destroy();
    }
    this.entryRefs = [];
  }

private captureCurrentScrollState(): void {

  const lightGrid = this.getLightGridEl();
  const darkGrid = this.getDarkGridEl();

  const lightDetails = this.getLightDetailsScrollEl();
  const darkDetails = this.getDarkDetailsScrollEl();

  let el: HTMLElement | null = null;

  if (this.currentViewState === 'list') {
    el =
      lightGrid && lightGrid.offsetParent !== null
        ? lightGrid
        : darkGrid;
  } else {
    el =
      lightDetails && lightDetails.offsetParent !== null
        ? lightDetails
        : darkDetails;
  }

  const key =
    this.currentViewState === 'list'
      ? 'blog-list'
      : 'blog-details';

  this.scroll.saveScrollPosition(key, el);
}

private restoreSharedScrollState(): void {
  if (this.currentViewState === 'list') {
    this.scroll.restoreScrollPositionAndResetKey(
      'blog-list',
      this.isDarkMode ? this.getDarkGridEl() : this.getLightGridEl()
    );
  } else {
    this.scroll.restoreScrollPositionAndResetKey(
      'blog-details',
      this.isDarkMode ? this.getDarkDetailsScrollEl() : this.getLightDetailsScrollEl()
    );
  }
}
  private getLightGridEl(): HTMLElement | null {
    return this.blogGridRef?.nativeElement ?? null;
  }

  private getDarkGridEl(): HTMLElement | null {
    return this.darkContainer?.querySelector('.blog-grid') as HTMLElement | null;
  }

  private getLightDetailsScrollEl(): HTMLElement | null {
    return this.blogDetailsRef?.nativeElement.querySelector('.scroll-bleed') as HTMLElement | null;
  }

  private getDarkDetailsScrollEl(): HTMLElement | null {
    return this.darkContainer?.querySelector('.blog-details-panel .scroll-bleed') as HTMLElement | null;
  }

}
